<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useGrid, useScreen } from 'vue-screen'
import DevicePreview from "@/components/DevicePreview.vue";
import HistorySection from "@/views/preview/sections/HistorySection.vue";
import HighlightBox from "@/views/preview/partials/HighlightBox.vue";
import ProofreadingSection from "@/views/preview/sections/ProofreadingSection.vue";
import AiReadinessSection from "@/views/preview/sections/AiReadinessSection.vue";
import CrossReferenceSection from "@/views/preview/sections/CrossReferenceSection.vue";
import QASection from "@/views/preview/sections/QASection.vue";
import OverviewSection from "@/views/preview/sections/OverviewSection.vue";
import AuditSection from "@/views/preview/sections/AuditSection.vue";
import RadialProgress from "vue3-radial-progress";
import { getProgressColor } from "@/utils";
import SectionSwitcher from "@/views/preview/SectionSwitcher.vue";
import { usePreviewLogic } from "@/composables/usePreviewLogic";
import TabbedSectionSwitcher from "@/views/preview/TabbedSectionSwitcher.vue";
import { observeInlineFaIcons } from '@/utils/inlineFaIcons';

const screen = useScreen()
const grid = useGrid('bootstrap5')
const previewRoot = ref(null)
let stopInlineFaObserver = () => {}

const {
  issueFilter,
  devicePreview,
  highlightBox,
  highlightDetails,
  historyPreview,
  filteredIssues,
  showBoxes,
  onLoaded,
  scroll,
  onManualScroll,
  onScrollToEntryCompleted,
  logClick,
  activeDeviceRender,
  route,
} = usePreviewLogic();

const maxWidth = computed(() => {
  if (grid.xl) {
    return 600;
  }
  if (grid.lg) {
    return 368;
  }
  return Math.min(screen.width - 100, 350)
})

onMounted(() => {
  stopInlineFaObserver = observeInlineFaIcons(previewRoot.value)
})

onBeforeUnmount(() => {
  stopInlineFaObserver()
})

</script>

<template>
  <div ref="previewRoot">
    <highlight-box ref="highlightBox" :highlight-details="highlightDetails"></highlight-box>

    <div v-if="activeDeviceRender.loaded" class="container standard-layout-parent" @click="logClick">
      <div class="row justify-content-end mt-2">
      <div class="col-12 col-md-6 col-xl-7">
        <div class="d-flex justify-content-center align-items-center mb-2"
             :class="`preview-${activeDeviceRender.devicePreview.deviceId}`">
          <table>
            <tbody>
            <tr>
              <td class="d-none d-lg-table-cell" style="width:30px">
                <button
                    type="button"
                    class="btn btn-sm btn-alt-secondary mb-1 ms-3 ms-xl-0"
                    v-tooltip="`Scroll Up`"
                    @click="scroll('up')"
                >
                  <i class="fa fa-fw fa-up-long"/>
                </button>
                <br>
                <button
                    type="button"
                    class="btn btn-sm btn-alt-secondary mb-1  ms-3 ms-xl-0"
                    @click="scroll('down')"
                    v-tooltip="`Scroll Down`"
                >
                  <i class="fa fa-fw fa-down-long"/>
                </button>
              </td>

              <td class="text-center">
                <div style="position: relative;white-space: nowrap;min-width:220px;" >
                  <device-preview ref="devicePreview" v-if="activeDeviceRender.loaded" :maxHeight="800"
                                  :maxWidth="maxWidth"
                                  :history="historyPreview"
                                  @loaded="onLoaded"
                                  :disableScreenshotMain="!!route.query.section"
                                  @on-manual-scroll="onManualScroll"
                                  @on-scroll-to-entry-completed="onScrollToEntryCompleted"
                                  v-bind="activeDeviceRender.devicePreview"></device-preview>
                  <br>

                </div>


              </td>
              <td>
              </td>
            </tr>
            </tbody>
          </table>

        </div>

      </div>
      <div
          class="col-12 col-md-6 col-xl-5 ps-2 pe-2 justify-content-end load-transition"
          style="border-left: 1px solid #80808021;">
        <div class="bg-white p-2" style="border-radius:6px">

          <div class="alert alert-danger my-3" role="alert"
               v-if="!activeDeviceRender.page.httpStatusOkOrPending">
            <p class="mb-0"><b>Page Load Error</b><br> We could not load this page, the server responded with a status
              code of {{ activeDeviceRender.page.httpStatusCode }}. </p>
          </div>


          <div v-else class="card border-0 shadow-sm mb-2 ">
            <div class="card-body p-0">
              <div class="d-flex align-items-stretch">
                <div
                    class="score-display d-flex flex-column align-items-center justify-content-center p-4 py-0 bg-light border-end">
                  <router-link :to="{query: {}}">
                  <RadialProgress
                      :diameter="80"
                      :completed-steps="activeDeviceRender.page?.score || 0"
                      :total-steps="100"
                      :startColor="getProgressColor(activeDeviceRender.page?.score || 0)"
                      :stopColor="getProgressColor(activeDeviceRender.page?.score || 0)"
                      :innerStrokeColor="'#e9ecef'"
                      :strokeWidth="6"
                      :innerStrokeWidth="6"
                  >
                    <div class="d-flex flex-column align-items-center">
                      <span class="h4 mb-0 fw-bold">{{ Math.round(activeDeviceRender.page?.score || 0) }}</span>
                      <span class="text-muted" style="font-size: 0.65rem; font-weight: 600;">Score</span>
                    </div>
                  <div class="spinner-border" v-if="!['completed', 'error'].includes(activeDeviceRender.page.status)"
                       style="position:absolute;font-size:16px;color:rgba(47,73,94,0.24);left:-2px;top:4px;width:72px;height:72px;">
                    <span class="sr-only">Loading..</span>
                  </div>
                  </RadialProgress>

                    </router-link>
                </div>
                <div class="p-4 flex-grow-1 d-flex flex-column justify-content-center overflow-hidden audit-summary">


                  <h4 class="mb-1 mt-2 text-dark fw-bold text-truncate" :title="activeDeviceRender.page?.title">
                    {{ activeDeviceRender.page?.title || 'Page Title' }}
                  </h4>
                  <div class="d-flex align-items-center text-muted small overflow-hidden">
                    <i class="fa fa-link me-2 flex-shrink-0"></i>
                    <a :href="activeDeviceRender.page?.url" target="_blank"
                       class="text-decoration-none text-muted text-truncate">
                      {{ activeDeviceRender.page?.url || 'https://example.com' }}
                    </a>
                    <i class="fa fa-external-link-alt ms-2 opacity-50 small flex-shrink-0"></i>
                  </div>

                </div>


              </div>

              <!-- Section Switcher Dropdown -->
              <tabbed-section-switcher :qa="true" />

            </div>

            <!-- Section Content -->
            <div class="max-height-overflow bg-white border-top">

              <template v-if="route.query.section === 'proofread'">
                <proofreading-section :devicePreview="devicePreview" v-if="devicePreview"></proofreading-section>
              </template>
              <template v-if="route.query.section === 'consistency'">
                <cross-reference-section :devicePreview="devicePreview" v-if="devicePreview"></cross-reference-section>
              </template>
              <template v-if="route.query.section === 'ai_readiness'">
                <ai-readiness-section :devicePreview="devicePreview" v-if="devicePreview"></ai-readiness-section>
              </template>
              <template v-if="route.query.section === 'history'">
                <history-section :devicePreview="devicePreview" v-if="devicePreview"/>
              </template>
              <template v-if="route.query.section === 'qa'">
                <q-a-section :devicePreview="devicePreview" v-if="devicePreview"/>
              </template>
              <template v-if="!route.query.section || route.query.section === 'audit'">
                <audit-section
                  :issueFilter="issueFilter"
                  @update:issueFilter="issueFilter = $event"
                  :filteredIssues="filteredIssues"
                  :showBoxes="showBoxes"
                />
              </template>
            </div>
          </div>
        </div>

      </div>
      </div>
    </div>
  </div>

</template>
<style>

.audit-evidence.audit-evidence-proofread::before {
  content: "PROOFREADING CHECKS";
}

.audit-evidence.audit-evidence-custom::before {
  content: var(--evidence-label, "");
}

.audit-evidence.audit-evidence-cross-page::before {
  content: "PAGE CONSISTENCY CHECKS";
}

.audit-evidence.audit-evidence-history::before {
  content: "PAGE HISTORY";
}



.infoblock .block-content {
  padding: 4px;
  font-size: 94%;
}

.issue-alert {
  background-color: red;
  z-index: 99;
  max-width: 450px;
  white-space: normal;
  color: white;
  padding: 4px;
  border-radius: 8px;
  font-size: 13px;

  span.fa-caret-down {
    color: red;
  }
}

.issue-alert .info {
  color: #1391aa;

  span.fa-caret-down {
    color: #1391aa;
  }
}

.issue-alert .danger {
  color: red;

  span.fa-caret-down {
    color: red;
  }
}

.issue-alert.info {
  background-color: #1391aa;

  span.fa-caret-down {
    color: #1391aa;
  }
}

.issue-alert-thin {
  max-width: 370px;
}

.issue-alert a {
  color: white;
  text-decoration: underline;
}

.issue-alert-code {
  margin-bottom: 0;
  overflow-wrap: break-word;
  margin-top: 4px;
  background-color: #464646;
  font-size: 11px;
  max-height: 54px;
}


</style>